// src/features/task/TaskAutomationPanel.tsx
// "Workflow" rail card — surfaces the ACTIVE, event-triggered pipelines that
// can run against tasks (trigger_event starting with "task."), so admins and
// task viewers can see which automations exist without leaving the task
// detail page. See docs/design/workspace-redesign/08-workflow-integration.md.
// Read-only: this panel does not run or manage pipelines, only points at them
// (workflow's own pages own execution, versions, and editing).

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWorkflow, useI18n } from '@react/hooks';
import { Card, CardHeader, CardTitle, CardContent, Badge } from '@react/components';
import { formatEnumLabel } from '@/features/task/logic';
import type { Pipeline } from '@core/types/workflow';

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function WorkflowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
    </svg>
  );
}

export function TaskAutomationPanel() {
  const store = useWorkflow();
  const { t } = useI18n();

  const [expanded, setExpanded] = useState(false);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loaded, setLoaded] = useState(false);

  // Lazy-load the first time the card is expanded.
  useEffect(() => {
    if (!expanded || loaded) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    store.ListPipelines()
      .then((res) => {
        if (cancelled) return;
        const items = (res?.items?.items || []).filter(
          (p) =>
            p.status === 'PIPELINE_STATUS_ACTIVE' &&
            p.trigger_type === 'TRIGGER_TYPE_EVENT' &&
            (p.trigger_event || '').startsWith('task.'),
        );
        setPipelines(items);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [expanded, loaded, store]);

  return (
    <Card>
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <WorkflowIcon className="h-4 w-4 text-muted-foreground" />
            <CardTitle>{t('task.automation.title')}</CardTitle>
          </div>
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={expanded ? t('common.collapse') : t('common.expand')}
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronIcon className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="h-9 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : loadError ? (
            <p className="text-sm text-destructive">{t('task.automation.loadError')}</p>
          ) : pipelines.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('task.automation.empty')}</p>
          ) : (
            <ul className="space-y-2">
              {pipelines.map((p) => {
                const id = p.entity?.id || '';
                return (
                  <li key={id} className="rounded-md border border-border px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <Link to={`/workflows/${id}`} className="text-sm font-medium text-foreground hover:text-primary hover:underline truncate">
                        {p.name}
                      </Link>
                      <Badge variant="secondary" size="sm" className="flex-shrink-0">
                        {formatEnumLabel(String(p.trigger_type || ''), 'TRIGGER_TYPE_')}
                      </Badge>
                    </div>
                    {p.trigger_event && (
                      <p className="mt-0.5 text-xs text-muted-foreground font-mono truncate">{p.trigger_event}</p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default TaskAutomationPanel;
